import { keyframes, styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { Meeting } from "@acto/taskpane/core/domain";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";

const Section = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const SectionLabel = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

const List = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
}));

const Row = styled("li")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const rowEnter = keyframes({
  from: { opacity: 0, transform: "translateY(8px)" },
  to: { opacity: 1, transform: "translateY(0)" },
});

const rowFade = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });

const MeetingRow = styled(Row)(({ theme }) => ({
  animation: `${rowEnter} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized} both`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: `${rowFade} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard} both`,
  },
}));

const STAGGER_STEP_MS = 40;
const MAX_STAGGERED_ROWS = 8;

const TimeChip = styled("span")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 56,
  height: 52,
  borderRadius: theme.fieldSalesTwo.radius[16],
  background: theme.fieldSalesTwo.colors.neutral[200],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Name = styled("span")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const RowInfo = styled("div")({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
});

const OwnerName = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

/** Scoped to the customer being planned for; colleagues' meetings elsewhere are not represented. */
export interface ColleagueMeeting {
  meeting: Meeting;
  /** Null when the colleague has no employee record to name. */
  ownerName: string | null;
}

const byStartTime =
  <T,>(startTimeOf: (item: T) => string | null | undefined) =>
  (a: T, b: T) =>
    (startTimeOf(a) ?? "99:99").localeCompare(startTimeOf(b) ?? "99:99");

interface PlannedMeetingsProps {
  /** The selected day's own meetings across all customers, including recurring occurrences. */
  meetings: Meeting[];
  /** The selected day's meetings at the customer being planned for that belong to colleagues. */
  colleagueMeetings?: ColleagueMeeting[];
}

export function PlannedMeetings({ meetings, colleagueMeetings = [] }: PlannedMeetingsProps) {
  const t = useTranslations("fieldSalesTwo.planMeeting");

  const sorted = [...meetings].sort(byStartTime((m: Meeting) => m.scheduledStartTime));

  const sortedColleagueMeetings = [...colleagueMeetings].sort(
    byStartTime((entry: ColleagueMeeting) => entry.meeting.scheduledStartTime),
  );

  if (!sorted.length && !sortedColleagueMeetings.length) return null;

  // All colleague rows share the customer the form is planning for, so any row names it.
  const customerName = sortedColleagueMeetings[0]?.meeting.customer.name;

  return (
    <>
      {sorted.length > 0 && (
        <Section>
          <SectionLabel>{t("plannedMeetings")}</SectionLabel>
          <List>
            {sorted.map((meeting, index) => (
              <MeetingRow
                key={meeting.id}
                style={{ animationDelay: `${Math.min(index, MAX_STAGGERED_ROWS) * STAGGER_STEP_MS}ms` }}
              >
                <TimeChip>{meeting.scheduledStartTime?.slice(0, 5)}</TimeChip>
                <Name>{meeting.customer.name}</Name>
              </MeetingRow>
            ))}
          </List>
        </Section>
      )}
      {sortedColleagueMeetings.length > 0 && (
        <Section>
          <SectionLabel>{t("otherMeetingsAt", { customer: customerName })}</SectionLabel>
          <List>
            {sortedColleagueMeetings.map(({ meeting, ownerName }, index) => (
              <MeetingRow
                key={meeting.id}
                style={{ animationDelay: `${Math.min(index, MAX_STAGGERED_ROWS) * STAGGER_STEP_MS}ms` }}
              >
                <TimeChip>{meeting.scheduledStartTime?.slice(0, 5)}</TimeChip>
                <RowInfo>
                  <Name>{meeting.customer.name}</Name>
                  {ownerName && <OwnerName>{ownerName}</OwnerName>}
                </RowInfo>
              </MeetingRow>
            ))}
          </List>
        </Section>
      )}
      <Divider />
    </>
  );
}
